import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { toast } from "sonner";
import Nav from "../components/Nav";
import Footer from "../components/Footer";

export default function SignUp() {
  const nav = useNavigate();
  const [mode, setMode] = useState("signup"); // signup | login
  const [form, setForm] = useState({ name: "", email: "", password: "", phone: "" });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const url = mode === "signup" ? "/users/signup" : "/users/login";
      const body = mode === "signup" ? form : { email: form.email, password: form.password };
      const r = await api.post(url, body);
      localStorage.setItem("ex_user_token", r.data.token);
      localStorage.setItem("ex_user_email", r.data.email);
      if (r.data.recommendation) {
        localStorage.setItem("ex_user_recommendation", JSON.stringify(r.data.recommendation));
      }
      toast.success(mode === "signup" ? "Welcome — let's find your fit." : "Welcome back.");
      // If they haven't taken the quiz, force them through it.
      if (r.data.needs_quiz) {
        nav("/quiz?from=signup");
      } else {
        nav("/learn");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Nav />
      <main className="flex-1 grid lg:grid-cols-2 max-w-7xl w-full mx-auto px-6 py-12 gap-12 items-center">
        {/* Pitch side */}
        <aside className="lg:pr-8">
          <p className="eyebrow"><span className="gold-divider" /> EXODUS LIFE & WEALTH</p>
          <h1 className="font-display text-4xl lg:text-5xl mt-3 leading-tight">
            One account.<br/>
            Three buckets.<br/>
            <span className="text-exodus-gold">Tax-free for life.</span>
          </h1>
          <p className="text-sm text-exodus-muted mt-5 max-w-md leading-relaxed">
            Take a 4-minute strategic fit assessment, get a personalized recommendation,
            and unlock a private library of education built around YOUR situation —
            not generic content. No spam. No high-pressure pitch.
          </p>
          <ul className="mt-8 space-y-3 text-sm text-exodus-text/80">
            {[
              "Personalized assessment with a written recommendation",
              "3-tab calculator: IUL · Annuity · Whole Life",
              "Audio library with 250 in-depth episodes",
              "Branded PDF Strategy Brief — yours to keep",
              "Optional 30-min private session with Leon — fully refundable deposit",
            ].map((x, i) => (
              <li key={i} className="flex items-start gap-3">
                <span className="text-exodus-gold mt-1">▸</span>
                <span>{x}</span>
              </li>
            ))}
          </ul>
        </aside>

        {/* Form side */}
        <section className="card-surface p-8 lg:p-10" data-testid="signup-form-card">
          <div className="flex gap-3 mb-6">
            <button
              data-testid="mode-signup"
              onClick={() => setMode("signup")}
              className={`text-sm px-4 py-2 border ${mode === "signup" ? "border-exodus-gold text-exodus-gold" : "border-exodus-border text-exodus-muted"}`}
            >
              Create account
            </button>
            <button
              data-testid="mode-login"
              onClick={() => setMode("login")}
              className={`text-sm px-4 py-2 border ${mode === "login" ? "border-exodus-gold text-exodus-gold" : "border-exodus-border text-exodus-muted"}`}
            >
              Log in
            </button>
          </div>

          <h2 className="font-display text-2xl">
            {mode === "signup" ? "Start your assessment" : "Welcome back"}
          </h2>
          <p className="text-xs text-exodus-muted mt-2">
            {mode === "signup"
              ? "Just an email + password — we don't sell your data, ever."
              : "Log in to continue where you left off."}
          </p>

          <form className="mt-6 space-y-4" onSubmit={submit}>
            {mode === "signup" && (
              <Field
                label="Your full name"
                testid="signup-name"
                value={form.name}
                onChange={(v) => setForm((s) => ({ ...s, name: v }))}
                required
              />
            )}
            <Field
              label="Email"
              type="email"
              testid={mode === "signup" ? "signup-email" : "login-email"}
              value={form.email}
              onChange={(v) => setForm((s) => ({ ...s, email: v }))}
              required
            />
            <Field
              label="Password"
              type="password"
              testid={mode === "signup" ? "signup-password" : "login-password"}
              value={form.password}
              onChange={(v) => setForm((s) => ({ ...s, password: v }))}
              required
            />
            {mode === "signup" && (
              <Field
                label="Phone (optional)"
                type="tel"
                testid="signup-phone"
                value={form.phone}
                onChange={(v) => setForm((s) => ({ ...s, phone: v }))}
              />
            )}

            <button
              data-testid="signup-submit"
              type="submit"
              disabled={busy}
              className="btn-primary w-full mt-6 disabled:opacity-50"
            >
              {busy ? "Please wait…" : mode === "signup" ? "Create account + start assessment" : "Log in"}
            </button>
          </form>

          {mode === "signup" && (
            <p className="text-[11px] text-exodus-muted mt-6 leading-relaxed">
              By creating an account you agree to receive a single educational email after each
              session. Unsubscribe anytime. We never sell, share, or rent your information.
            </p>
          )}
        </section>
      </main>
      <Footer />
    </div>
  );
}

function Field({ label, type = "text", value, onChange, required, testid }) {
  return (
    <label className="block">
      <span className="eyebrow text-[10px] block mb-2">{label}{required && " *"}</span>
      <input
        data-testid={testid}
        type={type}
        value={value}
        required={required}
        onChange={(e) => onChange(e.target.value)}
        className="w-full bg-transparent border border-exodus-border focus:border-exodus-gold focus:outline-none px-4 py-3 text-sm transition-colors"
      />
    </label>
  );
}
